import { Box, IconButton, InputAdornment, ListSubheader } from "@mui/material";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select, { type SelectChangeEvent } from "@mui/material/Select";
import { type ReactNode, useRef, useState } from "react";

import {
  dropdownMenuItemTextSx,
  getDropdownMenuItemSx,
  getDropdownMenuPropsSx,
  getMuiFilledSelectRootSx,
  muiFilledInputLabelSx,
  muiFilledSelectWrapperClassName,
} from "../features/shared/lib/muiDropdownStyles";
import { muiFilledInputLabelClasses } from "../features/shared/lib/muiFilledFieldStyles";
import { cn } from "../utils/styling";
import { Icon } from "./Icon";

type ChevronProps = Record<string, any> & { isOpen?: boolean };

const ChevronDownAndUp = ({ isOpen, ...props }: ChevronProps) => {
  const { className, style, ...rest } = props;

  return (
    <svg
      {...rest}
      className={cn("text-lds-foreground-primary", className)}
      fill="none"
      height="6"
      style={{
        cursor: "pointer",
        position: "absolute",
        right: "2rem",
        top: "calc(50% - 0.3rem)",
        transform: isOpen ? "rotate(180deg)" : "none",
        ...style,
      }}
      viewBox="0 0 11 6"
      width="11"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M1 0.750002L5.5 5.25L10 0.75" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
};

type MenuItemArgs = {
  displayOption: ReactNode;
  grouped?: boolean;
  key: string;
  selected?: boolean;
  value: any;
};

const MenuItemComponent = ({ displayOption, grouped, key, selected, value }: MenuItemArgs) => {
  return (
    <MenuItem key={key} selected={selected} sx={getDropdownMenuItemSx({ grouped })} value={value}>
      <ListItemText className="font-ds-body-regular-md" primary={displayOption} sx={dropdownMenuItemTextSx} />
    </MenuItem>
  );
};

export type DropdownSingleSelectProps<T = any> = Record<string, any> & {
  borderColor?: string;
  className?: string;
  clearable?: boolean;
  disabled?: boolean;
  displayOptions?: ReactNode[];
  hideLabel?: boolean;
  label?: ReactNode;
  onChange?: (event: SelectChangeEvent<any> | { target: { value: any } }) => void;
  options: readonly T[];
  optionsGroups?: { group?: string; options: T[] }[];
  placeholder?: ReactNode;
  readOnly?: boolean;
  selectedValue?: T;
  setSelectedValue: (value: T) => void;
  width?: number | string;
};

export const DropdownSingleSelect = ({
  borderColor,
  className,
  clearable = true,
  disabled,
  displayOptions,
  height = "4rem",
  hideLabel = false,
  label,
  onChange,
  options,
  optionsGroups,
  placeholder,
  readOnly = false,
  selectedValue,
  setSelectedValue,
  width,
  ...props
}: DropdownSingleSelectProps) => {
  const isGrouped = Boolean(optionsGroups?.length);
  const groups = isGrouped
    ? (optionsGroups as { group?: string; options: any[] }[])
    : [{ options: [...(options ?? [])] }];

  const resolvedDisplayOptions: ReactNode[] = displayOptions ?? (options as ReactNode[]);
  const hasSelectedValue = selectedValue !== undefined && selectedValue !== null && selectedValue !== "";
  const selectedOptionIndex = options?.findIndex((option) => option === selectedValue) ?? -1;
  const selectedOptionIsValid = !hasSelectedValue || selectedOptionIndex >= 0;
  const selectedDisplayValue = selectedOptionIsValid ? resolvedDisplayOptions?.[selectedOptionIndex] : selectedValue;
  const shouldShowLabel = !hideLabel && Boolean(label);
  const visiblePlaceholder = shouldShowLabel ? undefined : placeholder;
  const selectedDisplayClassName = cn(
    !hasSelectedValue && "text-lds-foreground-muted",
    !readOnly && !selectedOptionIsValid && "text-lds-purpose-status-negative-subtle"
  );

  const [isOpen, setIsOpen] = useState(false);

  const handleChange = (event: SelectChangeEvent<any>) => {
    const {
      target: { value },
    } = event;
    setSelectedValue(value);
    if (onChange) onChange(event);
  };

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => setIsOpen(false);

  const ref = useRef<HTMLDivElement | null>(null);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>({});
  const toggleGroup = (group: string) => {
    setOpenGroups((prev) => ({ ...prev, [group]: !prev[group] }));
  };

  if (readOnly) {
    return (
      <span className={cn(selectedDisplayClassName, className)} style={{ width }} {...props}>
        {hasSelectedValue ? selectedDisplayValue : visiblePlaceholder}
      </span>
    );
  }

  return (
    <div className={cn(muiFilledSelectWrapperClassName, className)} ref={ref} style={{ width }} {...props}>
      <FormControl hiddenLabel={hideLabel} sx={{ width: "100%" }} variant="filled">
        {shouldShowLabel && (
          <InputLabel
            classes={{
              root: muiFilledInputLabelClasses.root,
              shrink: muiFilledInputLabelClasses.shrink,
            }}
            sx={muiFilledInputLabelSx}
          >
            {label}
          </InputLabel>
        )}
        <Select
          className="font-ds-body-regular-md"
          disabled={disabled}
          displayEmpty
          endAdornment={
            clearable && hasSelectedValue ? (
              <InputAdornment className="mb-6 mr-1 w-0" position="end">
                <Icon
                  className="h-3 w-3"
                  iconName="Erase"
                  onClick={() => {
                    setSelectedValue(undefined);
                    onChange?.({
                      target: { value: undefined },
                    });
                  }}
                />
              </InputAdornment>
            ) : undefined
          }
          IconComponent={(p: any) => <ChevronDownAndUp isOpen={isOpen} {...p} />}
          MenuProps={{
            sx: getDropdownMenuPropsSx(ref.current?.offsetWidth, "strong"),
          }}
          onChange={handleChange}
          onClose={handleClose}
          onOpen={handleOpen}
          renderValue={() =>
            !hasSelectedValue ? (
              <span className={selectedDisplayClassName}>{visiblePlaceholder}</span>
            ) : !selectedOptionIsValid ? (
              <span className={selectedDisplayClassName}>{selectedDisplayValue}</span>
            ) : (
              selectedDisplayValue
            )
          }
          sx={getMuiFilledSelectRootSx({
            borderColor,
            hasValue: hasSelectedValue,
            height,
          })}
          value={selectedValue ?? ""}
        >
          {groups.map((group) => {
            const groupOptions = options?.filter((name) => group?.options?.includes(name));
            const groupDisplayOptions = resolvedDisplayOptions?.filter((_, index) =>
              groupOptions.includes(options[index])
            );
            const OptionsComponent = groupOptions?.map((name, index) =>
              MenuItemComponent({
                displayOption: groupDisplayOptions[index],
                grouped: isGrouped,
                key: String(name) + (group ?? ""),
                selected: name === selectedValue,
                value: name,
              })
            );
            if (isGrouped && groupOptions?.length && group.group) {
              const isOpen = openGroups[group.group];
              return [
                <ListSubheader className="w-full p-1" key={`subheader-${group.group}`}>
                  <Box
                    className={`${
                      isOpen ? "bg-lds-selection-strong " : "bg-lds-surface-subtle"
                    } flex w-full cursor-pointer items-center justify-between px-2 hover:bg-lds-selection-strong`}
                    onClick={() => toggleGroup(group.group!)}
                  >
                    <span className="font-ds-body-bold-md">{group.group}</span>
                    <IconButton size="small">
                      {isOpen ? <Icon iconName="ChevronDown" /> : <Icon iconName="ChevronRight" />}
                    </IconButton>
                  </Box>
                </ListSubheader>,
                isOpen && OptionsComponent,
              ];
            }

            return OptionsComponent;
          })}
        </Select>
      </FormControl>
    </div>
  );
};
